import React, { type FC, type PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { type ScrollMethod, useScrollSnap } from "../../../../../../../common-deprecated/hooks/useScrollSnap";
import { CarouselContext, useCarouselContext } from "./CarouselContext";
import styles from "./Carousel.module.scss";
import { useWindowDimensionsContext } from "../../../../../../../common-deprecated/containers/WindowDimensionsProvider";
import { useCommonSelector } from "../../../../../../../common-deprecated/redux/commonStore";

type CarouselTriggerProps = React.ComponentPropsWithoutRef<"button"> & {
    direction: "left" | "right";
};

const CarouselTrigger: FC<CarouselTriggerProps> = ({ children, direction, ...props }) => {
    const context = useCarouselContext();
    const isRtl = useCommonSelector((state) => state.commonSettings.culture.isRTL);

    const realDirection = isRtl ? (direction === "left" ? "right" : "left") : direction;

    const isDisabled =
        context.carouselProperties?.sliderDisabled ||
        (realDirection === "left" ? context.carouselProperties?.reachedStart : context.carouselProperties?.reachedEnd);

    return useMemo(
        () =>
            !context.carouselProperties ? null : (
                <button
                    {...props}
                    type="button"
                    disabled={isDisabled}
                    className={props.className}
                    onClick={(event) => context.carouselProperties?.handleChange(event, realDirection)}
                    aria-disabled={isDisabled}
                >
                    {children}
                </button>
            ),
        [
            context.carouselProperties?.sliderDisabled,
            context.carouselProperties?.reachedEnd,
            context.carouselProperties?.reachedStart,
            context.carouselProperties?.handleChange,
        ],
    );
};

const CarouselTrack: FC<PropsWithChildren<{ itemCount: number; scrollMethod: ScrollMethod }>> = ({
    itemCount,
    scrollMethod,
    children,
}) => {
    const context = useCarouselContext();
    const viewPortRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLUListElement>(null);
    const [sliderDisabled, setSliderDisabled] = useState(true);
    const windowWidth = useWindowDimensionsContext().width;

    // Enable the slider logic only if the slider is overflowing (items are hidden)
    useEffect(() => {
        if (sliderRef.current) {
            const slider = sliderRef.current;

            setSliderDisabled(slider.scrollWidth <= slider.clientWidth);
        }
    }, [windowWidth, sliderRef]);

    // TODO: Add tracking + check other properties?
    const carouselProperties = useScrollSnap({
        sliderRef,
        itemCount,
        scrollAmount: scrollMethod,
        disabled: sliderDisabled,
        intersectionThreshold: 1,
    });

    useEffect(() => {
        context.setCarouselProperties({ ...carouselProperties, sliderDisabled });
    }, [
        carouselProperties.reachedEnd,
        carouselProperties.reachedStart,
        carouselProperties.handleChange,
        carouselProperties.hasItemsToLeft,
        carouselProperties.hasItemsToRight,
    ]);

    return (
        <div className={styles["carousel-wrapper"]}>
            <div className={styles["carousel-container"]} ref={viewPortRef}>
                <ul role="list" className={styles["carousel-track"]} ref={sliderRef}>
                    {children}
                </ul>
            </div>
        </div>
    );
};

const Carousel: FC<PropsWithChildren> & {
    Trigger: typeof CarouselTrigger;
    Track: typeof CarouselTrack;
} = ({ children }) => {
    const [carouselProperties, setCarouselProperties] = useState<
        (ReturnType<typeof useScrollSnap> & { sliderDisabled: boolean }) | null
    >(null);

    return (
        <CarouselContext.Provider value={{ carouselProperties, setCarouselProperties }}>
            {children}
        </CarouselContext.Provider>
    );
};

Carousel.Trigger = CarouselTrigger;
Carousel.Track = CarouselTrack;

export default Carousel;
